import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  getEmergencyVisit,
  updateEmergencyVisit,
  addEmergencyCharge,
  listEmergencyCharges,
  deleteEmergencyCharge,
  updateEmergencyCharge,
} from "../../../api/emergency";
import { useAuth } from "../../../context";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  Autocomplete,
  CircularProgress,
  Alert,
  Snackbar,
  DialogContentText,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { getObjectsWithParams, addObject } from "../../../api/Api";
import { buildEmergencyPrintHtml } from "./PrintSummary";
import TicketPrint from "../Ticketing/TicketPrint";
import { Print as PrintIcon, Person as PersonIcon, PersonAdd as PersonAddIcon, CheckCircle as CheckCircleIcon, Warning as WarningIcon } from "@mui/icons-material";
import axios from "axios";

export default function EmergencyView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const { t } = useTranslation();
  const [visit, setVisit] = useState(null);
  const [charges, setCharges] = useState([]);
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [editingChargeForm, setEditingChargeForm] = useState({
    item_name: "",
    quantity: 1,
    unit_price: 0,
  });
  const [chargeForm, setChargeForm] = useState({
    item_name: "",
    quantity: 1,
    unit_price: 0,
  });
  const [patientSearchOpen, setPatientSearchOpen] = useState(false);
  const [patientQuery, setPatientQuery] = useState("");
  const [patientResults, setPatientResults] = useState([]);
  const [fee, setFee] = useState(0);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [admitType, setAdmitType] = useState(null); // 2=IPD, 3=ICU, 4=Referral, 5=Death
  const [admitNotes, setAdmitNotes] = useState("");
  const [dispoDoctor, setDispoDoctor] = useState("");
  const [admitDestination, setAdmitDestination] = useState("");
  const [admitBed, setAdmitBed] = useState("");
  const [refFacility, setRefFacility] = useState("");
  const [refContact, setRefContact] = useState("");
  const [refDoctor, setRefDoctor] = useState("");
  const [deathTime, setDeathTime] = useState("");
  const [deathCause, setDeathCause] = useState("");
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorValue, setDoctorValue] = useState(null);
  const [doctorInput, setDoctorInput] = useState("");

  // Vitals / triage / diagnosis / attending form
  const [infoForm, setInfoForm] = useState({
    bp: "",
    hr: "",
    rr: "",
    temp_c: "",
    spo2: "",
    chief_complaint: "",
    diagnosis: "",
    triage_level: "",
  });
  const [attendingOptions, setAttendingOptions] = useState([]);
  const [attendingValue, setAttendingValue] = useState(null);
  const [attendingInput, setAttendingInput] = useState("");
  const [attendingDoctor, setAttendingDoctor] = useState(null);
  const [dispositionDoctor, setDispositionDoctor] = useState(null);
  const [isFinalized, setIsFinalized] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [printTicketOpen, setPrintTicketOpen] = useState(false);
  const [ticketToPrint, setTicketToPrint] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const v = await getEmergencyVisit(id, token);
        setVisit(v);
        setFee(v?.er_visit_fee || 0);
        setIsFinalized(v?.is_finalized || false);
        setInfoForm({
          bp: v?.bp || "",
          hr: v?.hr ?? "",
          rr: v?.rr ?? "",
          temp_c: v?.temp_c ?? "",
          spo2: v?.spo2 ?? "",
          chief_complaint: v?.chief_complaint || "",
          diagnosis: v?.diagnosis || "",
          triage_level: v?.triage_level ?? "",
        });

        // Load doctor names
        if (v?.attending_doctor) {
          try {
            const doctor = await getObjectsWithParams(
              "users",
              { id: v.attending_doctor },
              token
            );
            const doctorData = doctor?.results?.[0] || doctor?.[0];
            if (doctorData) {
              setAttendingDoctor(doctorData);
              setAttendingValue(doctorData);
            }
          } catch (e) {
            console.error("Error loading attending doctor:", e);
          }
        }

        if (v?.disposition_doctor) {
          try {
            const doctor = await getObjectsWithParams(
              "users",
              { id: v.disposition_doctor },
              token
            );
            const doctorData = doctor?.results?.[0] || doctor?.[0];
            if (doctorData) {
              setDispositionDoctor(doctorData);
            }
          } catch (e) {
            console.error("Error loading disposition doctor:", e);
          }
        }

        const c = await listEmergencyCharges(id, token);
        setCharges(c?.results || c || []);
      } catch (err) {
        console.error("Error loading emergency visit:", err);
        setError(
          t("emergency.errors.load_failed", {
            defaultValue: "Failed to load emergency visit. Please try again.",
          })
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, token, t]);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        if (!doctorInput || doctorInput.trim().length < 2) return;
        const res = await getObjectsWithParams(
          "users",
          { search: doctorInput },
          token
        );
        const rows = res?.results || res || [];
        setDoctorOptions(rows);
      } catch (e) {
        // ignore
      }
    };
    fetchDoctors();
  }, [doctorInput, token]);

  // Fetch attending doctor options
  useEffect(() => {
    const fetchAttending = async () => {
      try {
        if (!attendingInput || attendingInput.trim().length < 2) return;
        const res = await getObjectsWithParams(
          "users",
          { search: attendingInput },
          token
        );
        const rows = res?.results || res || [];
        setAttendingOptions(rows);
      } catch (e) {
        // ignore
      }
    };
    fetchAttending();
  }, [attendingInput, token]);

  const saveInfo = async () => {
    const payload = {
      ...infoForm,
      triage_level: infoForm.triage_level
        ? Number(infoForm.triage_level)
        : null,
      hr: infoForm.hr === "" ? null : Number(infoForm.hr),
      rr: infoForm.rr === "" ? null : Number(infoForm.rr),
      temp_c: infoForm.temp_c === "" ? null : Number(infoForm.temp_c),
      spo2: infoForm.spo2 === "" ? null : Number(infoForm.spo2),
      attending_doctor: attendingValue?.id ?? visit?.attending_doctor ?? null,
    };
    const updated = await updateEmergencyVisit(id, payload, token);
    setVisit(updated);
  };

  const saveCharge = async (e) => {
    e.preventDefault();
    const c = await addEmergencyCharge(id, chargeForm, token);
    setCharges((prev) => [c, ...prev]);
    setChargeForm({ item_name: "", quantity: 1, unit_price: 0 });
  };

  const removeCharge = async (chargeId) => {
    const ok = window.confirm(
      t("emergency.confirm.delete_charge", {
        defaultValue: "Delete this charge?",
      })
    );
    if (!ok) return;
    await deleteEmergencyCharge(chargeId, token);
    setCharges((prev) => prev.filter((x) => x.id !== chargeId));
  };

  const startEditCharge = (charge) => {
    setEditingChargeId(charge.id);
    setEditingChargeForm({
      item_name: charge.item_name,
      quantity: Number(charge.quantity),
      unit_price: Number(charge.unit_price),
    });
  };

  const cancelEditCharge = () => {
    setEditingChargeId(null);
    setEditingChargeForm({ item_name: "", quantity: 1, unit_price: 0 });
  };

  const saveEditCharge = async () => {
    if (!editingChargeId) return;
    const payload = {
      item_name: editingChargeForm.item_name,
      quantity: Number(editingChargeForm.quantity),
      unit_price: Number(editingChargeForm.unit_price),
    };
    const updated = await updateEmergencyCharge(
      editingChargeId,
      payload,
      token
    );
    // Ensure total is consistent in UI
    const computedTotal = Number(payload.quantity) * Number(payload.unit_price);
    setCharges((prev) =>
      prev.map((c) =>
        c.id === editingChargeId
          ? { ...c, ...updated, ...payload, total_price: computedTotal }
          : c
      )
    );
    cancelEditCharge();
  };

  const openPatientModal = () => {
    setPatientSearchOpen(true);
    setPatientQuery("");
    setPatientResults([]);
  };

  const searchPatients = async () => {
    const data = await getObjectsWithParams(
      "patients",
      { search: patientQuery },
      token
    );
    const rows = data?.results || data || [];
    setPatientResults(rows);
  };

  const linkPatient = async (patientId) => {
    try {
      const updated = await updateEmergencyVisit(
        id,
        { patient: patientId },
        token
      );
      setVisit(updated);
      setPatientSearchOpen(false);
      
      // Wait a moment for the signal to create the ticket, then refetch to get updated ticket info
      setTimeout(async () => {
        try {
          const refreshed = await getEmergencyVisit(id, token);
          setVisit(refreshed);
          if (refreshed.ticket_number_display) {
            setSuccessMessage(
              t("emergency.messages.patient_linked_with_ticket", {
                defaultValue: `Patient linked successfully! Ticket ${refreshed.ticket_number_display} created.`,
              })
            );
          } else {
            setSuccessMessage(
              t("emergency.messages.patient_linked", {
                defaultValue: "Patient linked successfully. Ticket will be created automatically.",
              })
            );
          }
        } catch (err) {
          console.error("Error refreshing visit:", err);
        }
      }, 1000); // Wait 1 second for signal to complete
    } catch (err) {
      console.error("Error linking patient:", err);
      setError(
        t("emergency.errors.link_failed", {
          defaultValue: "Failed to link patient. Please try again.",
        })
      );
    }
  };

  const setDisposition = async (value) => {
    const updated = await updateEmergencyVisit(
      id,
      { disposition: value },
      token
    );
    setVisit(updated);
  };

  const openAdmit = (type) => {
    // For IPD admission (type 2), navigate directly to IPD wizard
    if (type === 2) {
      // Check if patient is linked
      if (!visit?.patient) {
        setError(
          t("emergency.errors.patient_required", {
            defaultValue: "Please link a patient before admitting to IPD.",
          })
        );
        return;
      }
      // Navigate to IPD admission wizard with emergency visit ID
      navigate(`/ipd/admissions/new?emergency=${id}`);
      return;
    }
    
    // For ICU admission (type 3), navigate directly to ICU wizard
    if (type === 3) {
      // Check if patient is linked
      if (!visit?.patient) {
        setError(
          t("emergency.errors.patient_required", {
            defaultValue: "Please link a patient before admitting to ICU.",
          })
        );
        return;
      }
      // Navigate to ICU admission wizard with emergency visit ID
      navigate(`/icu/admissions/new?emergency=${id}`);
      return;
    }
    
    // For other admission types (Referral, Death), open dialog
    setAdmitType(type);
    setAdmitNotes("");
    setDoctorOptions([]);
    setDoctorValue(null);
    setDoctorInput("");
    setDispoDoctor("");
    setAdmitDestination("");
    setAdmitBed("");
    setRefFacility("");
    setRefContact("");
    setRefDoctor("");
    setDeathTime("");
    setDeathCause("");
    setAdmitOpen(true);
  };

  const confirmAdmit = async () => {
    const payload = {
      disposition: admitType,
      disposition_notes: admitNotes,
      disposition_doctor: doctorValue?.id || null,
      admit_destination:
        admitType === 2 || admitType === 3 ? admitDestination || null : null,
      admit_bed_no:
        admitType === 2 || admitType === 3 ? admitBed || null : null,
      referral_facility: admitType === 4 ? refFacility || null : null,
      referral_contact: admitType === 4 ? refContact || null : null,
      referral_doctor: admitType === 4 ? refDoctor || null : null,
      death_time: admitType === 5 ? deathTime || null : null,
      death_cause: admitType === 5 ? deathCause || null : null,
    };
    const updated = await updateEmergencyVisit(id, payload, token);
    setVisit(updated);
    setAdmitOpen(false);
  };

  const finalizeVisit = async () => {
    setConfirmDialog({
      open: true,
      title: t("emergency.confirm.finalize_title", {
        defaultValue: "Finalize Visit",
      }),
      message: t("emergency.confirm.finalize", {
        defaultValue:
          "Are you sure you want to finalize this visit? This will send it to reception for payment.",
      }),
      onConfirm: async () => {
        try {
          const data = await addObject(
            `emergency-visits/${id}/finalize`,
            {},
            token
          );

          setVisit({
            ...visit,
            payment_status: 1,
            receipt_number: data.receipt_number,
          });
          setIsFinalized(true);
          setSuccessMessage(
            t("emergency.success.finalized", {
              defaultValue: `Visit finalized! Receipt: ${data.receipt_number}. Total: ${data.total_amount} AFN`,
            })
          );
        } catch (err) {
          console.error("Error finalizing visit:", err);
          setError(
            t("emergency.errors.finalize_failed", {
              defaultValue: "Failed to finalize visit. Please try again.",
            })
          );
        }
        setConfirmDialog({
          open: false,
          title: "",
          message: "",
          onConfirm: null,
        });
      },
    });
  };

  const reopenVisit = async () => {
    setConfirmDialog({
      open: true,
      title: t("emergency.confirm.reopen_title", {
        defaultValue: "Reopen Visit",
      }),
      message: t("emergency.confirm.reopen", {
        defaultValue: "Are you sure you want to reopen this visit for editing?",
      }),
      onConfirm: async () => {
        try {
          const updated = await updateEmergencyVisit(
            id,
            { is_finalized: false },
            token
          );
          setVisit(updated);
          setIsFinalized(false);
          setSuccessMessage(
            t("emergency.success.reopened", {
              defaultValue: "Visit has been reopened for editing.",
            })
          );
        } catch (err) {
          console.error("Error reopening visit:", err);
          setError(
            t("emergency.errors.reopen_failed", {
              defaultValue: "Failed to reopen visit. Please try again.",
            })
          );
        }
        setConfirmDialog({
          open: false,
          title: "",
          message: "",
          onConfirm: null,
        });
      },
    });
  };

  const printSummary = () => {
    const html = buildEmergencyPrintHtml({
      visit,
      charges,
      fee,
      t,
      mediaUrl:
        typeof process !== "undefined" &&
        process.env &&
        process.env.REACT_APP_MEDIA_URL
          ? process.env.REACT_APP_MEDIA_URL
          : "",
      attendingDoctor,
      dispositionDoctor,
    });
    const w = window.open("");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  const handlePrintTicket = async () => {
    if (!visit?.ticket) {
      setError(
        t("emergency.errors.no_ticket", {
          defaultValue: "No ticket available for this visit.",
        })
      );
      return;
    }

    try {
      setLoadingTicket(true);
      // Fetch full ticket data
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || "http://localhost:9000/api"}/tickets/${visit.ticket}/`,
        {
          headers: { Authorization: `Token ${token}` },
        }
      );
      setTicketToPrint(response.data);
      setPrintTicketOpen(true);
    } catch (err) {
      console.error("Error fetching ticket:", err);
      setError(
        t("emergency.errors.ticket_fetch_failed", {
          defaultValue: "Failed to load ticket data. Please try again.",
        })
      );
    } finally {
      setLoadingTicket(false);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {t("emergency.loading", {
            defaultValue: "Loading emergency visit...",
          })}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          {t("common.retry", { defaultValue: "Retry" })}
        </Button>
      </Box>
    );
  }

  if (!visit) return <Box p={2}>Loading...</Box>;

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 1 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
        >
          {t("emergency.labels.visit_information", {
            defaultValue: "Visit Information",
          })}
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("emergency.form.chief_complaint", {
                defaultValue: "Chief Complaint",
              })}
              value={infoForm.chief_complaint}
              onChange={(e) =>
                setInfoForm({ ...infoForm, chief_complaint: e.target.value })
              }
              placeholder={t("emergency.placeholders.enter_complaint", {
                defaultValue: "Enter chief complaint...",
              })}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("emergency.form.diagnosis", {
                defaultValue: "Diagnosis",
              })}
              value={infoForm.diagnosis}
              onChange={(e) =>
                setInfoForm({ ...infoForm, diagnosis: e.target.value })
              }
              placeholder={t("emergency.placeholders.enter_diagnosis", {
                defaultValue: "Enter diagnosis...",
              })}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              select
              label={t("emergency.form.triage_level", {
                defaultValue: "Triage Level",
              })}
              value={infoForm.triage_level}
              onChange={(e) =>
                setInfoForm({ ...infoForm, triage_level: e.target.value })
              }
              placeholder={t("emergency.placeholders.select_triage", {
                defaultValue: "Select triage level",
              })}
              variant="outlined"
              SelectProps={{ native: true }}
            >
              <option value=""></option>
              <option value={1}>
                {t("emergency.triage.red", { defaultValue: "Red" })}
              </option>
              <option value={2}>
                {t("emergency.triage.yellow", { defaultValue: "Yellow" })}
              </option>
              <option value={3}>
                {t("emergency.triage.green", { defaultValue: "Green" })}
              </option>
            </TextField>
          </Grid>
          <Grid item xs={12} md={8}>
            <Autocomplete
              fullWidth
              options={attendingOptions}
              value={attendingValue}
              onChange={(e, val) => setAttendingValue(val)}
              inputValue={attendingInput}
              onInputChange={(e, val) => setAttendingInput(val)}
              getOptionLabel={(o) =>
                ((o?.first_name || "") + " " + (o?.last_name || "")).trim() ||
                o?.username ||
                ""
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("emergency.form.attending_doctor", {
                    defaultValue: "Attending Doctor",
                  })}
                  placeholder={t("emergency.placeholders.search_doctors", {
                    defaultValue: "Search for doctors...",
                  })}
                  variant="outlined"
                />
              )}
            />
          </Grid>

          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label={t("emergency.form.bp", { defaultValue: "Blood Pressure" })}
              placeholder={t("emergency.placeholders.enter_bp", {
                defaultValue: "e.g. 120/80",
              })}
              value={infoForm.bp}
              onChange={(e) => setInfoForm({ ...infoForm, bp: e.target.value })}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              type="number"
              fullWidth
              label={t("emergency.form.hr", { defaultValue: "Heart Rate" })}
              value={infoForm.hr}
              onChange={(e) => setInfoForm({ ...infoForm, hr: e.target.value })}
              placeholder={t("emergency.placeholders.enter_hr", {
                defaultValue: "Enter heart rate...",
              })}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              type="number"
              fullWidth
              label={t("emergency.form.rr", {
                defaultValue: "Respiratory Rate",
              })}
              value={infoForm.rr}
              onChange={(e) => setInfoForm({ ...infoForm, rr: e.target.value })}
              placeholder={t("emergency.placeholders.enter_rr", {
                defaultValue: "Enter respiratory rate...",
              })}
              inputProps={{ min: 0 }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              type="number"
              fullWidth
              label={t("emergency.form.temp_c", {
                defaultValue: "Temperature (°C)",
              })}
              value={infoForm.temp_c}
              onChange={(e) =>
                setInfoForm({ ...infoForm, temp_c: e.target.value })
              }
              placeholder={t("emergency.placeholders.enter_temp", {
                defaultValue: "Enter temperature...",
              })}
              inputProps={{ min: 0, step: 0.1 }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <TextField
              type="number"
              fullWidth
              label={t("emergency.form.spo2", {
                defaultValue: "Oxygen Saturation (%)",
              })}
              value={infoForm.spo2}
              onChange={(e) =>
                setInfoForm({ ...infoForm, spo2: e.target.value })
              }
              placeholder={t("emergency.placeholders.enter_spo2", {
                defaultValue: "Enter oxygen saturation...",
              })}
              inputProps={{ min: 0, max: 100 }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 2,
              }}
            >
              <Button
                variant="contained"
                onClick={saveInfo}
                sx={{ borderRadius: 2, px: 3 }}
              >
                {t("emergency.buttons.save_changes", {
                  defaultValue: "Save Changes",
                })}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 1 }}>
        <Typography
          variant="h5"
          sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
        >
          {t("emergency.title", { defaultValue: "Emergency Visit" })} #
          {visit.id}
        </Typography>

        {/* Patient Status Indicator Card */}
        <Card
          sx={{
            mb: 3,
            border: 2,
            borderColor: visit.patient ? "success.main" : "warning.main",
            backgroundColor: visit.patient ? "success.light" : "warning.light",
            borderRadius: 2,
          }}
        >
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              {visit.patient ? (
                <>
                  <CheckCircleIcon sx={{ color: "success.main", fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      icon={<PersonIcon />}
                      label={t("emergency.status.patient_linked", {
                        defaultValue: "Patient Linked",
                      })}
                      color="success"
                      sx={{ fontWeight: "bold", fontSize: "0.9rem", mb: 1 }}
                    />
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: "bold" }}>
                      {visit.patient_details?.firstname || ""}{" "}
                      {visit.patient_details?.lastname || ""}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {visit.patient_details?.phone && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.phone", { defaultValue: "Phone" })}:</strong>{" "}
                            {visit.patient_details.phone}
                          </Typography>
                        </Grid>
                      )}
                      {visit.patient_details?.age && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.age", { defaultValue: "Age" })}:</strong>{" "}
                            {visit.patient_details.age}
                          </Typography>
                        </Grid>
                      )}
                      {visit.patient_details?.gender && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.gender", { defaultValue: "Gender" })}:</strong>{" "}
                            {visit.patient_details.gender}
                          </Typography>
                        </Grid>
                      )}
                      {visit.patient_details?.blood_type && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.blood_type", { defaultValue: "Blood Type" })}:</strong>{" "}
                            {visit.patient_details.blood_type}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </>
              ) : (
                <>
                  <WarningIcon sx={{ color: "warning.main", fontSize: 32 }} />
                  <Box sx={{ flex: 1 }}>
                    <Chip
                      icon={<PersonAddIcon />}
                      label={t("emergency.status.walkin_patient", {
                        defaultValue: "Walk-in Patient (Not Linked)",
                      })}
                      color="warning"
                      sx={{ fontWeight: "bold", fontSize: "0.9rem", mb: 1 }}
                    />
                    <Typography variant="h6" sx={{ mt: 1, fontWeight: "bold" }}>
                      {visit.walkin_name || t("emergency.labels.no_name", { defaultValue: "No Name Provided" })}
                    </Typography>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                      {visit.walkin_phone && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.phone", { defaultValue: "Phone" })}:</strong>{" "}
                            {visit.walkin_phone}
                          </Typography>
                        </Grid>
                      )}
                      {visit.walkin_age && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>{t("emergency.labels.age", { defaultValue: "Age" })}:</strong>{" "}
                            {visit.walkin_age}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={openPatientModal}
                      sx={{ mt: 2 }}
                    >
                      {t("emergency.buttons.link_patient", {
                        defaultValue: "Link Registered Patient",
                      })}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={6}>
            {visit.ticket_number_display && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>
                  {t("emergency.labels.ticket_number", {
                    defaultValue: "Ticket Number",
                  })}
                  :
                </strong>{" "}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    navigate(`/reception/ticketing/tickets/${visit.ticket}`)
                  }
                  sx={{ ml: 1, textTransform: "none" }}
                >
                  {visit.ticket_number_display}
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintTicket}
                  disabled={loadingTicket}
                  sx={{ ml: 1 }}
                >
                  {loadingTicket ? (
                    <CircularProgress size={16} />
                  ) : (
                    t("emergency.buttons.print_ticket", {
                      defaultValue: "Print Ticket",
                    })
                  )}
                </Button>
              </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>
                {t("emergency.labels.triage", { defaultValue: "Triage" })}:
              </strong>{" "}
              {{
                1: t("emergency.triage.red", { defaultValue: "Red" }),
                2: t("emergency.triage.yellow", { defaultValue: "Yellow" }),
                3: t("emergency.triage.green", { defaultValue: "Green" }),
              }[visit.triage_level] || "-"}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>
                {t("emergency.labels.disposition", {
                  defaultValue: "Disposition",
                })}
                :
              </strong>{" "}
              {{
                1: t("emergency.disposition.discharge", {
                  defaultValue: "Discharge",
                }),
                2: t("emergency.disposition.admit_ipd", {
                  defaultValue: "Admit IPD",
                }),
                3: t("emergency.disposition.admit_icu", {
                  defaultValue: "Admit ICU",
                }),
                4: t("emergency.disposition.referral", {
                  defaultValue: "Referral",
                }),
                5: t("emergency.disposition.death", { defaultValue: "Death" }),
              }[visit.disposition] || "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {attendingDoctor && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>
                  {t("emergency.labels.attending_doctor", {
                    defaultValue: "Attending Doctor",
                  })}
                  :
                </strong>{" "}
                {attendingDoctor.first_name} {attendingDoctor.last_name}
              </Typography>
            )}
            {dispositionDoctor && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>
                  {t("emergency.labels.diagnosing_doctor", {
                    defaultValue: "Disposition Doctor",
                  })}
                  :
                </strong>{" "}
                {dispositionDoctor.first_name} {dispositionDoctor.last_name}
              </Typography>
            )}
            {visit.disposition_notes && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>
                  {t("emergency.labels.notes", { defaultValue: "Notes" })}:
                </strong>{" "}
                {visit.disposition_notes}
              </Typography>
            )}
          </Grid>
        </Grid>
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            type="number"
            label={t("emergency.form.er_visit_fee", {
              defaultValue: "ER Visit Fee",
            })}
            value={fee}
            onChange={(e) => setFee(Number(e.target.value))}
            placeholder={t("emergency.placeholders.enter_fee", {
              defaultValue: "Enter ER visit fee",
            })}
            variant="outlined"
            sx={{ minWidth: 150 }}
            onBlur={async () => {
              const updated = await updateEmergencyVisit(
                id,
                { er_visit_fee: fee },
                token
              );
              setVisit(updated);
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {t("emergency.labels.charges_total", {
              defaultValue: "Charges Total",
            })}
            :{" "}
            <strong>
              {charges.reduce((s, c) => s + Number(c.total_price || 0), 0)}
            </strong>
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "primary.main" }}
          >
            {t("emergency.labels.grand_total", { defaultValue: "Grand Total" })}
            :{" "}
            <strong>
              {Number(fee || 0) +
                charges.reduce((s, c) => s + Number(c.total_price || 0), 0)}
            </strong>
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={printSummary}
            sx={{ borderRadius: 2 }}
          >
            {t("emergency.buttons.print_summary", {
              defaultValue: "Print Summary",
            })}
          </Button>
        </Box>
        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          {!isFinalized && (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={openPatientModal}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.link_patient", {
                  defaultValue: "Link Patient",
                })}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => setDisposition(1)}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.discharge", {
                  defaultValue: "Discharge",
                })}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => openAdmit(2)}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.admit_ipd", {
                  defaultValue: "Admit IPD",
                })}
              </Button>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                onClick={() => openAdmit(3)}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.admit_icu", {
                  defaultValue: "Admit ICU",
                })}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => openAdmit(4)}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.referral", { defaultValue: "Referral" })}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => openAdmit(5)}
                sx={{ borderRadius: 2 }}
              >
                {t("emergency.buttons.death", { defaultValue: "Death" })}
              </Button>
            </>
          )}
          {!isFinalized ? (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={finalizeVisit}
              sx={{ borderRadius: 2 }}
            >
              {t("emergency.buttons.finalize_visit", {
                defaultValue: "Finalize Visit",
              })}
            </Button>
          ) : (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={reopenVisit}
              sx={{ borderRadius: 2 }}
            >
              {t("emergency.buttons.reopen_visit", {
                defaultValue: "Reopen Visit",
              })}
            </Button>
          )}
        </Box>
      </Paper>

      {!isFinalized && (
        <Paper
          sx={{ p: 3, mb: 2, borderRadius: 2, boxShadow: 1 }}
          component="form"
          onSubmit={saveCharge}
        >
          <Typography
            variant="h6"
            sx={{ mb: 2, fontWeight: "bold", color: "primary.main" }}
          >
            {t("emergency.buttons.add_charge", { defaultValue: "Add Charge" })}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t("emergency.form.item_name", {
                  defaultValue: "Item Name",
                })}
                value={chargeForm.item_name}
                onChange={(e) =>
                  setChargeForm({ ...chargeForm, item_name: e.target.value })
                }
                placeholder={t("emergency.placeholders.enter_item_name", {
                  defaultValue: "Enter item name...",
                })}
                error={!chargeForm.item_name}
                helperText={
                  !chargeForm.item_name
                    ? t("emergency.validation.required", {
                        defaultValue: "This field is required",
                      })
                    : ""
                }
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                type="number"
                label={t("emergency.form.qty", { defaultValue: "Quantity" })}
                value={chargeForm.quantity}
                onChange={(e) =>
                  setChargeForm({
                    ...chargeForm,
                    quantity: Number(e.target.value),
                  })
                }
                placeholder={t("emergency.placeholders.enter_quantity", {
                  defaultValue: "Enter quantity...",
                })}
                inputProps={{ min: 0 }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                type="number"
                label={t("emergency.form.unit_price", {
                  defaultValue: "Unit Price",
                })}
                value={chargeForm.unit_price}
                onChange={(e) =>
                  setChargeForm({
                    ...chargeForm,
                    unit_price: Number(e.target.value),
                  })
                }
                placeholder={t("emergency.placeholders.enter_unit_price", {
                  defaultValue: "Enter unit price...",
                })}
                inputProps={{ min: 0 }}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ borderRadius: 2, height: "56px" }}
              >
                {t("emergency.buttons.add_charge", {
                  defaultValue: "Add Charge",
                })}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ borderRadius: 2, boxShadow: 1, overflow: "hidden" }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: "grey.100" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("emergency.form.item_name", { defaultValue: "Item Name" })}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("emergency.form.qty", { defaultValue: "Quantity" })}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("emergency.form.unit_price", { defaultValue: "Unit Price" })}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("emergency.labels.total", { defaultValue: "Total" })}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>
                {t("emergency.labels.actions", { defaultValue: "Actions" })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {charges.map((c) => {
              const isEditing = editingChargeId === c.id;
              const rowTotal = isEditing
                ? Number(editingChargeForm.quantity || 0) *
                  Number(editingChargeForm.unit_price || 0)
                : c.total_price;
              return (
                <TableRow key={c.id}>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        value={editingChargeForm.item_name}
                        onChange={(e) =>
                          setEditingChargeForm({
                            ...editingChargeForm,
                            item_name: e.target.value,
                          })
                        }
                      />
                    ) : (
                      c.item_name
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editingChargeForm.quantity}
                        onChange={(e) =>
                          setEditingChargeForm({
                            ...editingChargeForm,
                            quantity: Number(e.target.value),
                          })
                        }
                        inputProps={{ min: 0 }}
                        sx={{ maxWidth: 100 }}
                      />
                    ) : (
                      c.quantity
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editingChargeForm.unit_price}
                        onChange={(e) =>
                          setEditingChargeForm({
                            ...editingChargeForm,
                            unit_price: Number(e.target.value),
                          })
                        }
                        inputProps={{ min: 0 }}
                        sx={{ maxWidth: 120 }}
                      />
                    ) : (
                      c.unit_price
                    )}
                  </TableCell>
                  <TableCell>{rowTotal}</TableCell>
                  <TableCell align="right">
                    {!isFinalized &&
                      (isEditing ? (
                        <>
                          <Button
                            size="small"
                            onClick={cancelEditCharge}
                            sx={{ mr: 1 }}
                          >
                            {t("emergency.actions.cancel")}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={saveEditCharge}
                          >
                            {t("emergency.actions.save")}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="small"
                            onClick={() => startEditCharge(c)}
                            sx={{ mr: 1 }}
                          >
                            {t("emergency.actions.edit")}
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            onClick={() => removeCharge(c.id)}
                          >
                            {t("emergency.actions.delete")}
                          </Button>
                        </>
                      ))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={patientSearchOpen}
        onClose={() => setPatientSearchOpen(false)}
      >
        <DialogTitle>Link to Patient</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name or phone"
              value={patientQuery}
              onChange={(e) => setPatientQuery(e.target.value)}
            />
            <Button variant="contained" onClick={searchPatients}>
              Search
            </Button>
          </Box>
          <List dense>
            {patientResults.map((p) => (
              <ListItemButton key={p.id} onClick={() => linkPatient(p.id)}>
                <ListItemText
                  primary={
                    `${p.firstname || ""} ${p.lastname || ""}`.trim() ||
                    `Patient #${p.id}`
                  }
                  secondary={p.phone || ""}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPatientSearchOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        open={admitOpen}
        onClose={() => setAdmitOpen(false)}
      >
        <DialogTitle>
          {{ 2: "Admit to IPD", 3: "Admit to ICU", 4: "Referral", 5: "Death" }[
            admitType
          ] || "Disposition"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={doctorOptions}
                value={doctorValue}
                onChange={(e, val) => setDoctorValue(val)}
                inputValue={doctorInput}
                onInputChange={(e, val) => setDoctorInput(val)}
                getOptionLabel={(o) =>
                  ((o?.first_name || "") + " " + (o?.last_name || "")).trim() ||
                  o?.username ||
                  ""
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Diagnosing Doctor"
                    placeholder="Search user"
                  />
                )}
              />
            </Grid>
            {(admitType === 2 || admitType === 3) && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Destination / Ward"
                    value={admitDestination}
                    onChange={(e) => setAdmitDestination(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bed No"
                    value={admitBed}
                    onChange={(e) => setAdmitBed(e.target.value)}
                  />
                </Grid>
              </>
            )}
            {admitType === 4 && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Referral Facility"
                    value={refFacility}
                    onChange={(e) => setRefFacility(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Facility Contact"
                    value={refContact}
                    onChange={(e) => setRefContact(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Receiving Doctor"
                    value={refDoctor}
                    onChange={(e) => setRefDoctor(e.target.value)}
                  />
                </Grid>
              </>
            )}
            {admitType === 5 && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Time of Death"
                    value={deathTime}
                    onChange={(e) => setDeathTime(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Cause of Death"
                    value={deathCause}
                    onChange={(e) => setDeathCause(e.target.value)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={admitNotes}
                onChange={(e) => setAdmitNotes(e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdmitOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={confirmAdmit}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({
            open: false,
            title: "",
            message: "",
            onConfirm: null,
          })
        }
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({
                open: false,
                title: "",
                message: "",
                onConfirm: null,
              })
            }
          >
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
          <Button
            variant="contained"
            onClick={confirmDialog.onConfirm}
            autoFocus
          >
            {t("common.confirm", { defaultValue: "Confirm" })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Ticket Dialog */}
      {ticketToPrint && (
        <Dialog
          open={printTicketOpen}
          onClose={() => {
            setPrintTicketOpen(false);
            setTicketToPrint(null);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogContent>
            <TicketPrint
              ticket={ticketToPrint}
              onClose={() => {
                setPrintTicketOpen(false);
                setTicketToPrint(null);
              }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Success/Error Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccessMessage("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
